import { Button, Col, DatePicker, Flex, Form, Input, Row, Space, Tooltip } from 'antd'
import { translate } from '../../../core'
import styles from './OrderFormRepair.module.scss'
import { dateDisplay, dateFormat } from '../../../core/until'
import { PlusOutlined, PrinterOutlined, SaveOutlined } from '@ant-design/icons'
import { useContext, useEffect, useState } from 'react'
import {
  PageContextValue,
  QueryPageableRequest,
  QueryPageableResponse,
} from '../../../core/type'
import { Order, OrderEntry, OrderFindRequest, OrderRepair } from '../type'
import { OrderPageContext } from './OrderPageContext'
import { initNewRepairLine } from '../method/initNewRepairLine'
import {
  PrintRepairVariables,
  usePrinter,
} from '../../../core/component/Printer/printer.hook'
import { Contact, PhoneType } from '../../contact/type/contact'
import { gql, useLazyQuery } from '@apollo/client'
import { DraggableModal } from '../../../core/component'
import { ContactFindRequest } from '../../contact/type/contact.query'
import { GNPagination } from '../../../core/component/GNPagination'
import Enumerable from 'linq'

const { TextArea } = Input

export type OrderFormRepairProps = {
  open?: boolean
  version?: string
  onChangeVersion: (version: string) => void
  onClose: () => void
  onAmountChange: () => void
}

export const OrderFormRepair = ({
  open,
  onClose,
  version,
  onChangeVersion,
  onAmountChange,
}: OrderFormRepairProps) => {
  const [find] = useLazyQuery<
    QueryPageableResponse<Order, 'ordersFind'>,
    QueryPageableRequest<OrderFindRequest>
  >(gql`
    query GetOrders($request: OrderFindRequest) {
      ordersFind(request: $request) {
        data {
          number
          date
          repairs {
            version
            completionDate
            pickDate
            expectedDate
            item
            work
            specialInstruction
          }
          clerk
          addresses {
            street
            city
            state
            zipCode
            firstName
            lastName
          }
          customer {
            id
            lastName
            firstName
            phones {
              type
              number
            }
          }
        }
      }
    }
  `)
  const [findContacts] = useLazyQuery<
    QueryPageableResponse<Contact, 'contactsFind'>,
    QueryPageableRequest<ContactFindRequest>
  >(gql`
    query GetRelationsOfContact($request: ContactFindRequest!) {
      contactsFind(request: $request) {
        data {
          id
          relations {
            firstName
            lastName
            phones {
              number
            }
          }
        }
      }
    }
  `)
  const {
    form,
    entity: entityOnForm,
    isFormChanged,
  } = useContext<PageContextValue<Order, OrderEntry>>(OrderPageContext)
  const [index, setIndex] = useState<number>()
  const { print } = usePrinter()

  useEffect(() => {
    if (!open) return
    if (!version) return setIndex(0)

    const repairs = (form?.getFieldValue('repairs') as OrderRepair[]) ?? []
    let repair = repairs.find((r) => r.version === version)
    if (!repair) {
      repair = {
        version,
      } as OrderRepair
      repairs.push(repair)
      form?.setFieldValue('repairs', repairs)
    }

    setIndex(repairs.indexOf(repair))
  }, [version, open])

  const handleAdd = () => {
    const newVersion = initNewRepairLine(form)
    onChangeVersion(newVersion)
    onAmountChange()
  }

  const handlePrint = async () => {
    const id = entityOnForm?.id

    if (!id) return

    const { data } = await find({
      variables: {
        request: {
          id,
          take: 1,
          skip: 0,
        },
      },
    })

    const [entity] = data?.ordersFind.data ?? []

    if (!entity) return

    const { data: contactData } = await findContacts({
      variables: {
        request: {
          id: entity.customer.id,
          take: 1,
          skip: 0,
        },
      },
    })

    const [firstRelation] = contactData?.contactsFind.data[0]?.relations ?? []

    const repair = Enumerable.from(entity.repairs).orderBy(it => it.version).toArray()[index ?? 0]

    if (!repair) return

    const [address] = entity.addresses ?? []

    const totalNumberOfSpaceFromFirstUpToDate = 25
    const customerName = [address.lastName, address.firstName].filter(Boolean).join(',')
    // A name of 25 characters or more still gets one space before the date
    const customerLine = [
      customerName,
      ' '.repeat(Math.max(1, totalNumberOfSpaceFromFirstUpToDate - customerName.length)),
      dateDisplay(entity.date, dateFormat),
    ].join('')

    return print<PrintRepairVariables>({
      template_name: 'repair',
      variables: {
        title: `${entity.number} ${repair.version}`,
        twoCharacters: entity.clerk,
        shortDescription: `${address.street}`,
        address: [address.city, address.state, address.zipCode].filter(Boolean)
          .length
          ? `${address.city ?? ''}, ${address.state ?? ''} ${address.zipCode ?? ''
          }`
          : '',
        customerName: customerLine,
        homeNumber:
          entity.customer?.phones?.find((p) => p.type === PhoneType.Home)
            ?.number ?? '',
        cellNumber:
          entity.customer?.phones?.find((p) => p.type === PhoneType.Cell)
            ?.number ?? '',
        workNumber:
          entity.customer?.phones?.find((p) => p.type === PhoneType.Office)
            ?.number ?? '',
        altNumber: firstRelation?.phones[0]?.number?.toString() ?? '',
        date: repair?.completionDate
          ? dateDisplay(repair?.completionDate, dateFormat)
          : '',
        expectedDate: repair?.expectedDate
          ? dateDisplay(repair?.expectedDate, dateFormat)
          : '',
        item: repair?.item ?? '',
        work: repair?.work ?? '',
        specialInstruction: repair?.specialInstruction || '---',
        relation: [firstRelation?.lastName, firstRelation?.firstName]
          .filter(Boolean)
          .join(' '),
      },
    })
  }

  return (
    <DraggableModal
      maskClosable={false}
      style={{ top: 20 }}
      title={translate('order', 'repair')}
      closable
      cancelButtonProps={{ style: { display: 'none' } }}
      open={open}
      onCancel={onClose}
      footer={null}
      width={990}
    >

      <Form.List name='repairs'>
        {(fields) => {
          const resolvedIndex = fields.some((f) => f.name === index)
            ? index
            : fields[0]?.name
          return (
          <>
            {fields.map((field) => (
              <div
                style={{
                  display: field.name === resolvedIndex ? 'unset' : 'none',
                }}
              >
                <Flex className={styles.toolbar} align='center' gap={10}>
                  <Flex gap={10} align='center'>
                    <label>{translate('status')}</label>
                    <Form.Item
                      className={styles.inlineItem}
                      name={[field.name, 'status']}
                    >
                      <Input style={{ width: 100 }} />
                    </Form.Item>

                    <label>{translate('version')}</label>
                    <Form.Item
                      className={styles.inlineItem}
                      name={[field.name, 'version']}
                    >
                      <Input style={{ width: 60, textAlign: 'center' }} disabled />
                    </Form.Item>
                  </Flex>

                  <div style={{ flex: 1 }} />

                  <Flex align='center' gap={8}>
                    <Button onClick={handlePrint} icon={<PrinterOutlined />}>
                      {translate('print', 'label')}
                    </Button>

                    <Button
                      type={isFormChanged ? 'primary' : 'default'}
                      icon={<SaveOutlined />}
                      onClick={form?.submit}
                    >
                      {translate('save')}
                    </Button>

                    <Tooltip title={translate('new')}>
                      <Button
                        onClick={handleAdd}
                        icon={<PlusOutlined />}
                      />
                    </Tooltip>

                    <Form.Item
                      shouldUpdate
                      noStyle
                    >
                      {({ getFieldValue }) => {
                        const repairs = getFieldValue('repairs') as OrderRepair[]
                        return (
                          <GNPagination
                            page={(resolvedIndex ?? 0) + 1}
                            pageSize={1}
                            total={repairs.length}
                            onChange={page => setIndex(page - 1)}
                          />
                        )
                      }}
                    </Form.Item>
                  </Flex>
                </Flex>
                <Row gutter={[16, 16]} className={styles.fieldGrid}>
                  <Col span={8}>
                    <Form.Item
                      label={translate('completion', 'date')}
                      name={[field.name, 'completionDate']}
                    >
                      <DatePicker format={dateFormat} style={{ width: '100%' }} />
                    </Form.Item>
                  </Col>

                  <Col span={8}>
                    <Form.Item
                      label={translate('pick', 'date')}
                      name={[field.name, 'pickDate']}
                    >
                      <DatePicker format={dateFormat} style={{ width: '100%' }} />
                    </Form.Item>
                  </Col>

                  <Col span={8}>
                    <Form.Item
                      label={translate('expected', 'date')}
                      name={[field.name, 'expectedDate']}
                    >
                      <DatePicker format={dateFormat} style={{ width: '100%' }} />
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={[16, 16]} className={styles.fieldGrid}>
                  <Col span={24}>
                    <Form.Item
                      label={translate('item')}
                      name={[field.name, 'item']}
                    >
                      <TextArea size='large' rows={6} />
                    </Form.Item>
                  </Col>
                  <Col span={24}>
                    <Form.Item
                      className={styles.formItem}
                      label={translate('work')}
                      name={[field.name, 'work']}
                    >
                      <TextArea size='large' rows={6} />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item label={translate('by')} name={[field.name, 'by']}>
                      <Input />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item
                      label={translate('disposition')}
                      name={[field.name, 'disposition']}
                    >
                      <Input />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item
                      className={styles.formItem}
                      label={translate('storage')}
                      name={[field.name, 'storage']}
                    >
                      <Input />
                    </Form.Item>
                  </Col>
                  <Col span={24}>
                    <Space direction='vertical' className='w-100'>
                      <label>{translate('special_instruction')}</label>
                      <Form.Item
                        className={styles.formItem}
                        name={[field.name, 'specialInstruction']}
                      >
                        <TextArea size='large' rows={4} />
                      </Form.Item>
                    </Space>
                  </Col>
                </Row>
              </div>
            ))}
          </>
          )
        }}
      </Form.List>
    </DraggableModal>
  )
}
